import axiosApiClient from '..';
import { handleError, throwErrorIfWrongStatus } from './utils';

const BASE_URL = '/d/influencers';

class UserService {
  fetchUserInfo = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/me`, { withCredentials: true });
      throwErrorIfWrongStatus(response, 200);
      const { user, accessToken } = response.data.data;
      return { user, accessToken };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user's info.");
    }
  };

  changePassword = async ({ currentPassword, password }) => {
    try {
      const body = {
        currentPassword,
        password,
      };
      const response = await axiosApiClient.put(`${BASE_URL}/change-password`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, "Error occurred while changing user's password.");
    }
  };

  fetchUserEarnings = async ({ startDate, endDate, earningType }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/earnings?startDate=${startDate}&endDate=${endDate}&earningType=${earningType}`,
      );
      throwErrorIfWrongStatus(response, 200);
      const { daily, overview } = response.data.data;
      return { daily, overview };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user's earnings.");
    }
  };

  fetchUserMonthlyEarnings = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/earnings/monthly`);
      throwErrorIfWrongStatus(response, 200);
      const { monthlyInfluencerEarnings } = response.data.data;
      return { monthlyInfluencerEarnings };
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user's monthly earnings.");
    }
  };

  handleWithdrawalMethods = async body => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/withdrawal-method`, body);
      throwErrorIfWrongStatus(response, 200);
      const { withdrawalMethods } = response.data.data;
      return { withdrawalMethods };
    } catch (err) {
      throw handleError(err, 'Error occurred while handling withdrawal methods.');
    }
  };

  updateBasicInfo = async formData => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/update-basic-info`, formData, { headers });
      throwErrorIfWrongStatus(response, 200);
      const { user } = response.data.data;
      return { user };
    } catch (err) {
      throw handleError(err, 'Error occurred while updating basic info.');
    }
  };

  fetchInfluencers = async ({ page }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}?page=${page}&search=`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total } = response.data.data;
      return { data, total };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching influencers.');
    }
  };

  fetchAlbumMedia = async (tag, kind, page, userInfluencerId, exactMatch, folderId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (tag) params.append('tag', tag.join(','));
      if (kind) params.append('kind', kind);
      if (page) params.append('page', page);
      if (exactMatch) params.append('exactMatch', exactMatch);
      if (userInfluencerId) params.append('userInfluencerId', userInfluencerId);
      if (folderId) params.append('assetFolderId', folderId);
      if (influencerId) params.append('influencerId', influencerId);

      const response = await axiosApiClient.get(`${BASE_URL}/album-media-crm?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data, total, perPage, counts } = response.data.data;
      return { data, maxReached: total <= +page * +perPage, page, counts };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching album media.');
    }
  };

  fetchMediaBundles = async (userInfluencerId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      if (userInfluencerId) params.append('userInfluencerId', userInfluencerId);
      const response = await axiosApiClient.get(`${BASE_URL}/media-bundles?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { data } = response.data.data;
      return { data };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching media bundles.');
    }
  };

  editAlbumMedia = async (mediaId, body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.put(`${BASE_URL}/album-media/${mediaId}?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
      const { media } = response.data.data;
      return { media };
    } catch (err) {
      throw handleError(err, 'Error occurred while editing album media.');
    }
  };

  uploadPhoto = async (formData, onUploadProgress) => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-photo`, formData, {
        headers,
        onUploadProgress,
      });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading photo.');
    }
  };

  uploadVideo = async (formData, onUploadProgress) => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-video`, formData, {
        headers,
        onUploadProgress,
      });
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  deleteAlbumMedia = async (albumMediaId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.delete(`${BASE_URL}/${albumMediaId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      return { albumMediaId };
    } catch (err) {
      throw handleError(err, 'Error occurred while deleting album media');
    }
  };

  uploadBundle = async (formData, onUploadProgress) => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-bundle`, formData, {
        headers,
        onUploadProgress,
      });
      throwErrorIfWrongStatus(response, 200);
      const { bundle } = response.data.data;
      return { bundle };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading bundle.');
    }
  };

  uploadBundleV2 = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/upload-bundle/v2?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading bundle.');
    }
  };

  fetchBundleById = async (bundleId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.get(`${BASE_URL}/media-bundle/${bundleId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      const { bundle } = response.data.data;
      return { bundle };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching bundle.');
    }
  };

  deleteBundle = async (bundleId, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.delete(`${BASE_URL}/media-bundle/${bundleId}?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      return { bundleId };
    } catch (err) {
      throw handleError(err, 'Error occurred while deleting bundle.');
    }
  };

  editBundle = async (bundleId, body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.put(`${BASE_URL}/media-bundle/${bundleId}?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
      return { bundleId, name: body.name };
    } catch (err) {
      throw handleError(err, 'Error occurred while editing bundle.');
    }
  };

  fetchReferrals = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/referrals`);
      throwErrorIfWrongStatus(response, 200);
      const { referrals } = response.data.data;
      return { referrals };
    } catch (err) {
      throw handleError(err, 'Error occurred while fetching referrals.');
    }
  };

  uploadW9Form = async formData => {
    try {
      const headers = { 'Content-Type': 'multipart/form-data' };
      const response = await axiosApiClient.post(`${BASE_URL}/upload-w9`, formData, {
        headers,
      });
      throwErrorIfWrongStatus(response, 200);
      const { w9Info } = response.data.data;
      return { w9Info };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading w9 form.');
    }
  };

  startUploadAssistedPhoto = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/upload-photo/assisted/start?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
      const { url, filename } = response.data.data;
      return { url, filename };
    } catch (err) {
      throw handleError(err, 'Error occurred while starting upload photo.');
    }
  };

  completeUploadAssistedPhoto = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/upload-photo/assisted/complete-upload?${params.toString()}`,
        body,
      );
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while completing photo upload.');
    }
  };

  startUploadStory = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/upload-story/start?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
      const { url, filename } = response.data.data;
      return { url, filename };
    } catch (err) {
      throw handleError(err, 'Error occurred while starting upload story.');
    }
  };

  completeUploadStory = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/upload-story/complete?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
    } catch (err) {
      throw handleError(err, 'Error occurred while completing story upload.');
    }
  };

  startUploadAssistedVideo = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/upload-video/assisted/start?${params.toString()}`, body);
      throwErrorIfWrongStatus(response, 200);
      const { uploadId, filename } = response.data.data;
      return { uploadId, filename };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  getUploadAssistedVideoUrl = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/upload-video/assisted/get-upload-url?${params.toString()}`,
        body,
      );
      throwErrorIfWrongStatus(response, 200);
      const { url } = response.data.data;
      return { url };
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  completeUploadAssistedVideo = async (body, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(
        `${BASE_URL}/upload-video/assisted/complete-upload?${params.toString()}`,
        body,
      );
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading video.');
    }
  };

  testVoice = async (message, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/test-voice-eleven-labs?${params.toString()}`, {
        message,
      });
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while testing voice.');
    }
  };

  editManualChatTotalSpentThreshold = async (manualChatTotalSpentThreshold, influencerId) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.put(`${BASE_URL}/manual-chat-spent-threshold?${params.toString()}`, {
        manualChatTotalSpentThreshold,
      });
      throwErrorIfWrongStatus(response, 200);
      return manualChatTotalSpentThreshold;
    } catch (err) {
      throw handleError(err, 'Error occurred while editing manualChatTotalSpentThreshold.');
    }
  };

  uploadMediaFromDropbox = async (influencerId, dropboxPath, folders) => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/dropbox-upload?${params.toString()}`, {
        dropboxPath,
        ...(folders && { folders }),
      });
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while uploading media from dropbox');
    }
  };

  getDropboxSyncStatus = async influencerId => {
    try {
      const params = new URLSearchParams();
      if (influencerId) params.append('influencerId', influencerId);
      const response = await axiosApiClient.post(`${BASE_URL}/dropbox-sync-status?${params.toString()}`);
      throwErrorIfWrongStatus(response, 200);
      return response.data.data;
    } catch (err) {
      throw handleError(err, 'Error occurred while getting media sync from dropbox status');
    }
  };

  getOrCreateIdVerificationToken = async () => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/id-verification`);
      throwErrorIfWrongStatus(response, 200);
      const { sessionUrl, sessionStatus } = response.data.data;
      return { sessionUrl, sessionStatus };
    } catch (err) {
      throw handleError(err, 'Error occurred while getting identify verification token.');
    }
  };
}

export default new UserService();
